import {
  getFirstResultItem,
  getResultItems,
  getResultMessage,
  isSuccessfulResult,
  unwrapOperationResult,
} from './clientEntryContracts';

export const CLIENT_ACTIVITY_ITEM_NAMES = Object.freeze({
  workspace: 'ClientCommandWorkspace',
});

export const CLIENT_ACTIVITY_OPERATIONS = Object.freeze({
  aiQuestion: 'AIQuestionClientActivity',
  search: 'SearchClientActivity',
  loadDetail: 'LoadClientActivityDetail',
  resolveSourceRoute: 'ResolveClientActivitySourceRecordRoute',
  exportSearch: 'ExportClientActivitySearch',
});

export const CLIENT_ACTIVITY_EXPORT_FORMATS = Object.freeze([
  { Text: 'CSV', Value: 'csv', Sequence: 1 },
  { Text: 'Excel (XLSX)', Value: 'xlsx', Sequence: 2 },
]);

const ACTIVITY_AI_FILTER_FIELDS = Object.freeze([
  'FromUtc',
  'ToUtc',
  'Text',
  'Codes',
  'Sources',
  'Statuses',
  'Actor',
  'Principal',
  'Provider',
  'CorrelationId',
  'TargetRecord',
]);

const ACTIVITY_AI_FILTER_LOOKUP = new Map(
  ACTIVITY_AI_FILTER_FIELDS.map((fieldName) => [
    fieldName.toLowerCase(),
    fieldName,
  ]),
);

const ACTIVITY_SOURCE_VALUES = new Set([
  'Event',
  'StatusLog',
  'Log',
  'ClientResourceActivity',
]);

export const normalizeActivityAIFilter = (value, appliedSearch) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const source = value.Filter ?? value.filter ?? value.Output ?? value.output
    ?? value;
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    return null;
  }
  const proposed = { ...appliedSearch, Skip: 0, Continuation: '' };
  for (const [fieldName, fieldValue] of Object.entries(source)) {
    const canonical = ACTIVITY_AI_FILTER_LOOKUP.get(
      String(fieldName).toLowerCase(),
    );
    if (!canonical) return null;
    if (canonical === 'Codes'
      || canonical === 'Sources'
      || canonical === 'Statuses') {
      if (!Array.isArray(fieldValue)
        || fieldValue.length > 50
        || fieldValue.some((item) => (
          typeof item !== 'string' || item.trim().length > 100
        ))) return null;
      const normalizedValues = [...new Set(fieldValue
        .map((item) => item.trim())
        .filter(Boolean))];
      if (canonical === 'Sources'
        && normalizedValues.some((item) => !ACTIVITY_SOURCE_VALUES.has(item))) {
        return null;
      }
      proposed[canonical] = normalizedValues;
    } else {
      proposed[canonical] = fieldValue;
    }
  }
  return proposed;
};

export const createDefaultActivitySearch = (
  now = new Date(),
) => {
  const to = new Date(now);
  const from = new Date(to);
  from.setUTCDate(from.getUTCDate() - 31);
  return {
    FromUtc: from.toISOString(),
    ToUtc: to.toISOString(),
    Text: '',
    Code: '',
    Codes: [],
    Source: '',
    Sources: [],
    ApplicationSource: '',
    Category: '',
    Status: '',
    Statuses: [],
    Actor: '',
    Principal: '',
    Provider: '',
    CorrelationId: '',
    TargetRecord: '',
    SortBy: 'TimestampUtc',
    SortDirection: 'Descending',
    PageSize: 50,
    Skip: 0,
    Continuation: '',
  };
};

export const selectActivityMetadata = (
  result,
  propertyName,
) => {
  const values = result?.[propertyName] ?? result?.[
    propertyName.charAt(0).toLowerCase() + propertyName.slice(1)
  ];
  if (Array.isArray(values)) return values[0] ?? null;
  if (typeof values?.first === 'function') return values.first() ?? null;
  return values ?? null;
};

export const buildActivityWorkspaceRequest = ({
  clientId,
  currentEnvironment,
  search,
  source,
  exportFormat,
}) => ({
  ClientId: clientId,
  CurrentEnvironment: currentEnvironment,
  ActivityExportFormat: exportFormat || '',
  ActivitySearchRequest: search
    ? { ...search }
    : null,
  ActivitySource: source
    ? {
      SourceKey: source.SourceKey,
      SourceItemType: source.SourceItemType,
      SourceItemId: source.SourceItemId,
    }
    : null,
});

export const activityQueryFamilyKey = (search, take) => JSON.stringify({
  FromUtc: search?.FromUtc,
  ToUtc: search?.ToUtc,
  Text: search?.Text || '',
  Code: search?.Code || '',
  Codes: [...(search?.Codes || [])].sort(),
  Source: search?.Source || '',
  Sources: [...(search?.Sources || [])].sort(),
  ApplicationSource: search?.ApplicationSource || '',
  Category: search?.Category || '',
  Status: search?.Status || '',
  Statuses: [...(search?.Statuses || [])].sort(),
  Actor: search?.Actor || '',
  Principal: search?.Principal || '',
  Provider: search?.Provider || '',
  CorrelationId: search?.CorrelationId || '',
  TargetRecord: search?.TargetRecord || '',
  SortBy: search?.SortBy || 'TimestampUtc',
  SortDirection: search?.SortDirection || 'Descending',
  PageSize: Number(take || search?.PageSize || 50),
});

export const buildActivityTableSearch = (
  activeSearch,
  query,
  continuation = '',
) => ({
  ...activeSearch,
  SortBy: query?.SortBy || activeSearch?.SortBy || 'TimestampUtc',
  SortDirection: String(query?.SortOrder || '').toUpperCase() === 'ASC'
    ? 'Ascending'
    : 'Descending',
  PageSize: Number(query?.Take || activeSearch?.PageSize || 50),
  Skip: Math.max(0, Number(query?.Skip || 0)),
  Continuation: continuation,
});

export const activitySourceReference = (row) => ({
  SourceKey: row?.SourceKey || row?.Source || '',
  SourceItemType: row?.SourceItemType || '',
  SourceItemId: row?.SourceItemId || '',
});

export const runActivityOperation = async (
  manager,
  operationName,
  item,
  fallbackMessage,
) => {
  if (!manager?.RunOperation) throw new Error(fallbackMessage);
  const result = await manager.RunOperation({
    operationName,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: false,
  });
  if (!isSuccessfulResult(result)) {
    throw new Error(getResultMessage(result, fallbackMessage));
  }
  return getFirstResultItem(result);
};

export const runActivitySearchOperation = async (
  manager,
  item,
  fallbackMessage,
) => {
  if (!manager?.RunOperation) throw new Error(fallbackMessage);
  const operationResult = await manager.RunOperation({
    operationName: CLIENT_ACTIVITY_OPERATIONS.search,
    item,
    isItem: true,
    isWarning: true,
    supressAllErrors: false,
  });
  const result = unwrapOperationResult(operationResult);
  if (!result || result.Success === false) {
    throw new Error(getResultMessage(operationResult, fallbackMessage));
  }
  const items = getResultItems(result);
  return {
    ...result,
    Items: items,
    TotalItemCount: Number.isFinite(result?.TotalItemCount)
      ? result.TotalItemCount
      : items.length,
  };
};

export const decodeActivityExport = (exportItem) => {
  const content = String(exportItem?.ContentBase64 || '');
  const binary = globalThis.atob(content);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new Blob([bytes], {
    type: exportItem?.ContentType || 'text/csv',
  });
};
